import { AppContext } from 'providers/context/App'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import {
  INFLUENCER_ENHANCEMENT_PRICE,
  INFLUENCER_FACIAL_PRICE,
} from 'utils/constants/Helpers'
import { MONTHLY_SECOND_FACIAL_PRICE } from 'utils/constants/Membership'
import { formattedCost, getFacialType } from 'utils/helper-functions'
import { getEnhancementPrice } from 'utils/helper-functions/membershipInfo'
import useNavigate from 'utils/navigation/useCustomNavigate'
import { Facial as FacialType } from 'utils/types/appTypes'
import { OptionsTypes } from 'utils/types/enhancementTypes'
import { ServiceContent } from 'utils/types/maliblePageTypes'
import useMembership from 'views/hooks/useMembership'
import useServices from 'views/hooks/useServices'

type FacialServiceType = FacialType & ServiceContent

type Props = {
  setTotal: Dispatch<SetStateAction<number>>
  isUpsell?: boolean
}

const AppointmentSubtotal = ({ isUpsell, setTotal }: Props) => {
  const { appMasterData } = useContext(AppContext)
  const {
    isMember,
    isMonthlyMember,
    isBiMonthlyMember,
    isBiMonthlyVoucherActive,
    isMonthlyVoucherActive,
  } = useMembership()
  const { isInfluencerFlow } = useNavigate()
  const { facialName } = useServices()

  const isDevEnv = process.env.REACT_APP_ENV === 'development'
  const isLaserFacial = appMasterData.isLaserFacial
  const isMicroNeedling = appMasterData.isMicroNeedling
  const purchasedMembership = appMasterData.cart.purchasedMembership
  const isLaserConsultation =
    appMasterData.category.categoryObj?.id ===
      (isDevEnv
        ? appMasterData.selectedService?.boulevard_sandbox_id
        : appMasterData.selectedService?.boulevard_id) && isLaserFacial

  const selectedTier = appMasterData.location.tier?.toLowerCase()

  // calculating subtotal when enhacement removed or membership removed
  useEffect(() => {
    getSubTotal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appMasterData.selectedEnhancements,
    appMasterData.cart.purchasedMembership,
  ])

  const calculateFacialPrice = useCallback((): number => {
    if (!appMasterData?.category) return 0

    const {
      category: { categoryObj },
    } = appMasterData

    const actualFacialPrice = categoryObj?.listPrice ?? 0

    const tierPrice = appMasterData.selectedService?.[
      selectedTier.toLowerCase() as keyof FacialServiceType
    ] as number

    const specialPricingRules: Array<() => number | undefined> = [
      () => (!isMember || isLaserFacial ? actualFacialPrice : undefined),
    ]

    // Pick the first valid price
    const memberPrice = specialPricingRules
      .map((rule) => rule())
      .find((price) => price !== undefined)

    return memberPrice ?? tierPrice ?? actualFacialPrice
  }, [appMasterData, isLaserFacial, isMember, selectedTier])

  const calculateEnhancementsPrice = useCallback(
    (basePrice: number): number => {
      return isMember && !isLaserFacial
        ? getEnhancementPrice(basePrice, isBiMonthlyMember)
        : basePrice
    },
    [isBiMonthlyMember, isLaserFacial, isMember]
  )

  const getSubTotal = useCallback((): number => {
    const membershipCost =
      isUpsell || purchasedMembership
        ? appMasterData.membershipData.listPrice
        : 0

    let enhancementsPrice = (
      appMasterData.selectedEnhancements as OptionsTypes[]
    ).reduce((acc, curr) => acc + (curr.listPrice ?? 0), 0)

    let facialPrice = calculateFacialPrice()
    enhancementsPrice = calculateEnhancementsPrice(enhancementsPrice)

    if (isUpsell || purchasedMembership) {
      facialPrice = 0
      enhancementsPrice = getEnhancementPrice(
        enhancementsPrice,
        isBiMonthlyMember
      )
    }

    let discountPrice = appMasterData.cart.summary.discountAmount

    if (isInfluencerFlow) {
      facialPrice = INFLUENCER_FACIAL_PRICE
      enhancementsPrice = INFLUENCER_ENHANCEMENT_PRICE
    }

    const subTotal =
      membershipCost + facialPrice + enhancementsPrice - discountPrice

    setTotal(subTotal)
    return subTotal
  }, [
    appMasterData.cart.summary.discountAmount,
    appMasterData.membershipData.listPrice,
    appMasterData.selectedEnhancements,
    calculateEnhancementsPrice,
    calculateFacialPrice,
    isBiMonthlyMember,
    isInfluencerFlow,
    isUpsell,
    purchasedMembership,
    setTotal,
  ])

  // Monthly membership price. Only show if upsell or purchased
  const membershipPriceJSX = useMemo(() => {
    if (!purchasedMembership || !isUpsell) return null

    return (
      <div className="price-item">
        <span className="item-text">Monthly Membership</span>
        <span className="cost mono-1">
          {formattedCost(appMasterData.membershipData.listPrice)}
        </span>
      </div>
    )
  }, [appMasterData, isUpsell, purchasedMembership])

  // Facial price line, if member or upsell or purchased, show discounted price as well
  const facialPriceJSX = useMemo(() => {
    const getMemberServicePrice = () => {
      if (isMicroNeedling && isBiMonthlyMember) {
        return formattedCost(
          appMasterData.category.categoryObj?.listPrice,
          true,
          100
        )
      }
      if (!isLaserFacial && !isLaserConsultation) {
        // NOSONAR
        return formattedCost(
          Math.max(
            isMonthlyVoucherActive
              ? MONTHLY_SECOND_FACIAL_PRICE
              : appMasterData.membershipData.listPrice,
            8500
          )
        )
      } else if (isLaserFacial && !isLaserConsultation) {
        const tierPrice = appMasterData.selectedService?.[
          selectedTier.toLowerCase() as keyof FacialServiceType
        ] as number
        return formattedCost(tierPrice, true)
      }
    }

    return (
      <div className="price-item">
        <span
          className={`item-text ${purchasedMembership ? 'strike-through' : ''}`}
        >
          {isInfluencerFlow ? facialName : getFacialType(appMasterData)}
        </span>
        <span>
          <span
            className={`cost mono-1 ${
              ((isMember &&
                !isLaserConsultation &&
                isMicroNeedling === false &&
                (isMonthlyMember || !isBiMonthlyVoucherActive)) ||
                isUpsell ||
                purchasedMembership ||
                isInfluencerFlow) &&
              !isBiMonthlyMember &&
              'strike-through'
            }`}
          >
            {formattedCost(
              Number(appMasterData.category.categoryObj?.listPrice)
            )}
          </span>
          {(isUpsell || purchasedMembership) && (
            <span className="cost mono-1 member-price success">
              {formattedCost(0)}
            </span>
          )}
          {isInfluencerFlow && (
            <span className="cost mono-1 success member-price">
              {formattedCost(INFLUENCER_FACIAL_PRICE)}
            </span>
          )}
          {!isInfluencerFlow &&
            !isUpsell &&
            !purchasedMembership &&
            isMember &&
            (isMonthlyMember || !isBiMonthlyVoucherActive) &&
            isMicroNeedling === false &&
            !isBiMonthlyMember &&
            !isLaserConsultation && (
              <span className="cost mono-1 member-price success">
                {/* reduce upto $31 from facial price for members. minimum price should be $85. */}
                {/* facial price - $31 OR $85. whichever is higher */}
                {getMemberServicePrice()}
              </span>
            )}
        </span>
      </div>
    )
  }, [
    appMasterData,
    isLaserFacial,
    isMicroNeedling,
    isUpsell,
    purchasedMembership,
    facialName,
    isInfluencerFlow,
    isBiMonthlyMember,
    isMonthlyMember,
    isBiMonthlyVoucherActive,
    isMember,
    isMonthlyVoucherActive,
    isLaserConsultation,
    selectedTier,
  ])

  // Enhancements price list, if member or upsell or purchased, show discounted price as well
  const enhancementsPriceListJSX = useMemo(() => {
    return (
      <>
        {!isLaserFacial &&
          (appMasterData.selectedEnhancements as OptionsTypes[]).map((item) => (
            <div className="price-item" key={item.id}>
              <span className="item-text">{item.name}</span>
              <div>
                <span
                  className={`cost mono-1 ${
                    isMember ||
                    isUpsell ||
                    purchasedMembership ||
                    isInfluencerFlow
                      ? 'strike-through'
                      : ''
                  }`}
                >
                  {formattedCost(item.listPrice)}
                </span>
                {(isMember ||
                  isUpsell ||
                  purchasedMembership ||
                  isInfluencerFlow) && (
                  <span className="cost mono-1 member-price success">
                    {isInfluencerFlow
                      ? formattedCost(INFLUENCER_ENHANCEMENT_PRICE)
                      : formattedCost(
                          getEnhancementPrice(item.listPrice, isBiMonthlyMember)
                        )}
                  </span>
                )}
              </div>
            </div>
          ))}
      </>
    )
  }, [
    appMasterData,
    isLaserFacial,
    isMember,
    isUpsell,
    purchasedMembership,
    isBiMonthlyMember,
    isInfluencerFlow,
  ])

  const savingsText = useMemo(() => {
    if (selectedTier === 'tier1') return '35% savings'
    if (selectedTier === 'tier2') return '37% savings'
    return ''
  }, [selectedTier])

  // Membership benefit. remaining amount to be shown as discount to make the price of facial as per requirements
  const membershipBenefitJSX = useMemo(() => {
    return (
      <>
        {isInfluencerFlow && (
          <div className="price-item">
            <span className="item-text success">Partner Discount Applied</span>
          </div>
        )}
        {isMember && !isLaserConsultation && isMonthlyMember && (
          <div className="price-item">
            <span className="item-text success">
              Member Benefit {!isLaserFacial && 'Applied!'}
            </span>
            {(isLaserFacial || appMasterData.isMicroNeedling) && (
              <div>
                <span className="cost mono-1 member-price success">
                  {savingsText}
                </span>
              </div>
            )}
          </div>
        )}
      </>
    )
  }, [
    isMember,
    isLaserFacial,
    appMasterData.isMicroNeedling,
    isLaserConsultation,
    isInfluencerFlow,
    isMonthlyMember,
    savingsText,
  ])

  // Promo code discount line, show as received from getCart call
  const promoCodeDiscountLineJSX = useMemo(() => {
    return (
      <>
        {appMasterData.cart.isPromoCodeApplied && (
          <div className="price-item">
            <span className="item-text success promocode-applied">
              {appMasterData.cart.promoCodes.map((d) => d.code).join(', ')}
            </span>
            <span>
              <span className="cost mono-1 success promocode-applied">
                -{formattedCost(appMasterData.cart.summary.discountAmount)}
              </span>
            </span>
          </div>
        )}
      </>
    )
  }, [appMasterData])

  // Total line, show only if more then one lines from above is shown
  const totalLineJSX = useMemo(() => {
    return (
      <>
        {(!!appMasterData.selectedEnhancements.length ||
          (isMember && !isLaserFacial) ||
          isUpsell ||
          purchasedMembership ||
          appMasterData.cart.isPromoCodeApplied) && (
          <div
            className={`price-item total ${purchasedMembership ? 'membership-purchased' : ''}`}
          >
            <span
              className={`item-text ${
                isUpsell || purchasedMembership ? 'success' : ''
              }`}
            >
              {!purchasedMembership && isUpsell ? 'Member Price' : 'Total'}
            </span>
            <span className="cost mono-1 success">
              {formattedCost(Math.max(getSubTotal(), 0))}
            </span>
          </div>
        )}
      </>
    )
  }, [
    appMasterData,
    isUpsell,
    purchasedMembership,
    getSubTotal,
    isLaserFacial,
    isMember,
  ])

  // total amount to be paid on checkout. only membership purchase is charged on card on checkout
  const amountPaidTodayJSX = useMemo(() => {
    if (!purchasedMembership) return null

    return (
      <>
        <div className="price-item">
          <span className="item-text">Amount Paid Today</span>
          <div>
            <span className="cost mono-1 amount-pay-today">
              {formattedCost(appMasterData.membershipData.listPrice)}
            </span>
          </div>
        </div>
        <span className="body-2 note">
          Adding enhancement charges will be processed after your appointment.
        </span>
      </>
    )
  }, [appMasterData, purchasedMembership])

  return (
    <>
      {membershipPriceJSX}
      {facialPriceJSX}
      {enhancementsPriceListJSX}
      {membershipBenefitJSX}
      {promoCodeDiscountLineJSX}
      {totalLineJSX}
      {amountPaidTodayJSX}
    </>
  )
}

export default AppointmentSubtotal
